import React, { useRef, useEffect } from 'react';

const DnaHelixCanvas = () => {
    const canvasRef = useRef(null);

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx = canvas.getContext('2d');
        let animationFrameId;

        // Configuration
        const isMobile = window.innerWidth < 768;
        const particleCount = isMobile ? 140 : 260; // Denser look
        const verticalSpeed = 0.5; // Slow vertical flow
        const rotationSpeed = 0.003; // Extremely slow phase rotation
        const strandRadius = isMobile ? 60 : 100; // Width of the helix
        const waveLength = 0.015; // Vertical tightness

        let time = 0;
        let particles = [];

        // Resize handler
        const resize = () => {
            const parent = canvas.parentElement;
            const dpr = window.devicePixelRatio || 1;
            canvas.width = parent.clientWidth * dpr;
            canvas.height = parent.clientHeight * dpr;
            ctx.scale(dpr, dpr);
            canvas.style.width = `${parent.clientWidth}px`;
            canvas.style.height = `${parent.clientHeight}px`;
        };
        resize();
        window.addEventListener('resize', resize);

        // Initialize particles
        // We simulate a cylinder: angle (theta) around the y-axis
        for (let i = 0; i < particleCount; i++) {
            particles.push({
                y: Math.random() * canvas.height / (window.devicePixelRatio || 1), // Spread vertically
                angle: Math.random() * Math.PI * 2, // Angle around the helix
                speed: 0.2 + Math.random() * 0.3, // Vertical flow speed variation
                baseSize: 2 + Math.random() * 3, // Min 2px, Max 5px
                color: Math.random() > 0.6 ? '#ff7a00' : '#333333', // Accent vs Dark
                strand: Math.random() > 0.5 ? 0 : 1, // Phase A or Phase B (Double Helix)
            });
        }

        const render = () => {
            // Get dimensions ensuring DPR is accounted for logical calcs
            const width = canvas.width / (window.devicePixelRatio || 1);
            const height = canvas.height / (window.devicePixelRatio || 1);
            const centerX = width / 2;

            ctx.clearRect(0, 0, width, height);

            time += rotationSpeed;

            // Sort particles by "depth" (z) so we draw back-to-front
            // This is crucial for the 3D feel
            // We'll calculate their current 3D position first without drawing
            particles.forEach(p => {
                p.y -= verticalSpeed;
                if (p.y < -50) p.y = height + 50; // Wrap around

                // Calculate Helix Position
                // Phase determines the y-offset for the sine wave
                const phaseOffset = p.strand === 0 ? 0 : Math.PI;
                const currentAngle = (p.y * waveLength) + time + phaseOffset + (p.angle * 0.1); // angle factor adds some "cloud" width

                // X position: sin of angle
                // Z position: cos of angle (for depth)
                p.currentX = centerX + Math.cos(currentAngle) * strandRadius;
                p.currentZ = Math.sin(currentAngle); // -1 (back) to 1 (front)
            });

            // Sort: -1 (back) drawn first, 1 (front) drawn last
            particles.sort((a, b) => a.currentZ - b.currentZ);

            // Draw Connections (Rungs)
            // We simulate rungs by connecting random particles that are close in Y but opposite in Strand
            // Optimization: Only check a subset or purely geometric lines
            ctx.lineWidth = 1;
            const totalRungs = Math.floor(height / 30); // Approx spacing

            for (let r = 0; r < totalRungs; r++) {
                const rungY = (r * 30 + (time * 100)) % (height + 100) - 50;
                // Calculate exact strand positions at this Y
                const angle = (rungY * waveLength) + time;
                const x1 = centerX + Math.cos(angle) * strandRadius;
                const x2 = centerX + Math.cos(angle + Math.PI) * strandRadius;

                // Depth for alpha
                // The rung itself rotates, so its visibility might oscillate, but usually we just want it subtle
                const z = Math.sin(angle); // Depth of strand 1

                ctx.beginPath();
                ctx.moveTo(x1, rungY);
                ctx.lineTo(x2, rungY);

                const alpha = 0.1 + (z + 1) * 0.05; // 0.1 to 0.2 opacity
                ctx.strokeStyle = `rgba(100, 116, 139, ${alpha})`; // Slate-500 equivalent with alpha
                ctx.stroke();
            }

            // Draw Particles
            particles.forEach(p => {
                // Depth logic
                // Scale: 0.5 (back) to 1.5 (front)
                const scale = 0.6 + ((p.currentZ + 1) / 2) * 0.8;
                // Opacity: 0.3 (back) to 1 (front)
                const alpha = 0.3 + ((p.currentZ + 1) / 2) * 0.7;

                ctx.globalAlpha = alpha;

                // Glow only for front particles or accent ones
                if (p.color === '#ff7a00' && p.currentZ > 0) {
                    ctx.shadowBlur = 15;
                    ctx.shadowColor = '#ff7a00';
                } else {
                    ctx.shadowBlur = 0;
                }

                ctx.fillStyle = p.color;
                ctx.beginPath();
                ctx.arc(p.currentX, p.y, p.baseSize * scale, 0, Math.PI * 2);
                ctx.fill();

                // Reset context for next particle
                ctx.globalAlpha = 1;
                ctx.shadowBlur = 0;
            });

            animationFrameId = requestAnimationFrame(render);
        };

        render();

        return () => {
            window.removeEventListener('resize', resize);
            cancelAnimationFrame(animationFrameId);
        };
    }, []);

    return <canvas ref={canvasRef} className="block w-full h-full" style={{ background: 'transparent' }} />;
};

export default DnaHelixCanvas;
